import React from 'react';
import PropTypes from 'prop-types';

const SECTION_CLASS = 'overflow-hidden rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-sm)]';
const SECTION_HEADER_CLASS = 'border-b border-[var(--color-border)] bg-[var(--off)] px-[22px] py-4';
const SECTION_LABEL_CLASS = 'text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]';

export function OutlineButton({ children, onClick, className = '', submit = false, ...rest }) {
  return (
    <button
      type={submit ? 'submit' : 'button'}
      onClick={onClick}
      className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--n900)] transition-colors hover:bg-[var(--off)] font-[family-name:var(--font-ui)] ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

OutlineButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  submit: PropTypes.bool,
};

const MONO_LABELS = new Set(['IBAN', 'BIC', 'Postal', 'Registration no.', 'VAT ID', 'Customer no.']);

const inlineInputClassName =
  'min-w-[140px] max-w-[65%] h-9 rounded-lg border border-[var(--color-border)] bg-[#f5f6f8] px-3 text-right text-[13.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--n800)]/20';

export function SettingsInfoRow({
  label,
  value,
  mono = false,
  isLast = false,
  editing = false,
  editable = false,
  inputType = 'text',
  onChange,
}) {
  const displayValue = value === null || value === undefined || value === '' ? '—' : value;
  const showInput = editing && editable && onChange;

  return (
    <div
      className={`flex items-center justify-between gap-[18px] px-[22px] py-3.5 ${isLast ? '' : 'border-b border-[var(--color-border)]'}`}
    >
      <span className='shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{label}</span>
      {showInput ? (
        <input
          type={inputType}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inlineInputClassName} ${mono ? 'tracking-[0.02em] [font-feature-settings:"tnum"]' : ''}`}
        />
      ) : (
        <span
          className={`max-w-[65%] break-words text-right text-[13.5px] font-medium text-[var(--n900)] ${
            mono ? 'font-[family-name:var(--font-ui)] tracking-[0.02em] [font-feature-settings:"tnum"]' : ''
          }`}
        >
          {displayValue}
        </span>
      )}
    </div>
  );
}

SettingsInfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mono: PropTypes.bool,
  isLast: PropTypes.bool,
  editing: PropTypes.bool,
  editable: PropTypes.bool,
  inputType: PropTypes.string,
  onChange: PropTypes.func,
};

export function ProfileInfoSection({ title, rows, editing, onFieldChange, className = '', id }) {
  if (!rows || rows.length === 0) return null;

  return (
    <section id={id} className={`${SECTION_CLASS} ${className}`}>
      <div className={SECTION_HEADER_CLASS}>
        <div className={SECTION_LABEL_CLASS}>{title}</div>
      </div>
      <div>
        {rows.map((row, index) => (
          <SettingsInfoRow
            key={row.label}
            label={row.label}
            value={row.value}
            mono={row.mono ?? MONO_LABELS.has(row.label)}
            isLast={index === rows.length - 1}
            editing={editing}
            editable={row.editable}
            inputType={row.inputType || 'text'}
            onChange={row.field && onFieldChange ? (nextValue) => onFieldChange(row.field, nextValue) : undefined}
          />
        ))}
      </div>
    </section>
  );
}

ProfileInfoSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      field: PropTypes.string,
      editable: PropTypes.bool,
      inputType: PropTypes.string,
      mono: PropTypes.bool,
    }),
  ).isRequired,
  editing: PropTypes.bool,
  onFieldChange: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
};

export function SettingsInfoSection({ title, rows, className = '', id }) {
  if (!rows || rows.length === 0) return null;

  return (
    <section id={id} className={`${SECTION_CLASS} ${className}`}>
      <div className={SECTION_HEADER_CLASS}>
        <div className={SECTION_LABEL_CLASS}>{title}</div>
      </div>
      <div>
        {rows.map(([label, value], index) => (
          <SettingsInfoRow
            key={label}
            label={label}
            value={value}
            mono={['IBAN', 'BIC', 'Postal', 'Registration no.', 'VAT ID', 'Customer no.'].includes(label)}
            isLast={index === rows.length - 1}
          />
        ))}
      </div>
    </section>
  );
}

SettingsInfoSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)).isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
};

export function SecurityActionRow({ title, description, badge, action, isLast = false }) {
  return (
    <div
      className={`flex items-center justify-between gap-[18px] px-[22px] py-4 ${isLast ? '' : 'border-b border-[var(--color-border)]'}`}
    >
      <div className='min-w-0 flex-1'>
        <div className='mb-0.5 flex items-center gap-2.5'>
          <span className='text-[13.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>{title}</span>
          {badge}
        </div>
        {description && <div className='text-xs text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{description}</div>}
      </div>
      {action}
    </div>
  );
}

SecurityActionRow.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  badge: PropTypes.node,
  action: PropTypes.node.isRequired,
  isLast: PropTypes.bool,
};

export function ToggleSwitch({ checked, onChange, ariaLabel }) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      className={`relative h-[22px] w-10 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors duration-150 ${
        checked ? 'bg-[var(--n800)]' : 'bg-[var(--n200,#d4d8df)]'
      }`}
    >
      <span
        className='absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-[left] duration-150'
        style={{ left: checked ? 20 : 2 }}
      />
    </button>
  );
}

ToggleSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

export function SettingsPanel({ title, children, className = '', id }) {
  return (
    <section id={id} className={`${SECTION_CLASS} ${className}`}>
      <div className={SECTION_HEADER_CLASS}>
        <div className={SECTION_LABEL_CLASS}>{title}</div>
      </div>
      <div>{children}</div>
    </section>
  );
}

SettingsPanel.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string,
};

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isConfirming = false,
  confirmTone = 'primary',
}) {
  if (!isOpen) return null;

  const confirmClassName =
    confirmTone === 'danger'
      ? 'bg-[var(--color-error)] text-white hover:opacity-90'
      : 'bg-[var(--n800)] text-white hover:opacity-90';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black/50'
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role='button'
        tabIndex={0}
        aria-label='Close modal'
      />
      <div
        className='relative z-10 mx-4 w-full max-w-md rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-6 shadow-[var(--shadow-lg)]'
        role='dialog'
        aria-modal='true'
        aria-labelledby='confirmation-modal-title'
      >
        <h3 id='confirmation-modal-title' className='mb-2 text-lg font-medium text-[var(--n900)]'>
          {title}
        </h3>
        <p className='mb-6 text-sm text-[var(--text-secondary)]'>{message}</p>
        <div className='flex justify-end gap-3'>
          <button
            type='button'
            onClick={onClose}
            disabled={isConfirming}
            className='inline-flex h-10 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-4 text-sm font-medium text-[var(--n800)] hover:bg-[var(--off)] disabled:cursor-not-allowed disabled:opacity-50'
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={isConfirming}
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${confirmClassName}`}
          >
            {isConfirming ? 'Updating…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  isConfirming: PropTypes.bool,
  confirmTone: PropTypes.oneOf(['primary', 'danger']),
};

export const accountCardClass = 'rounded-2xl border border-[var(--color-border)] bg-[var(--white)] p-7 shadow-[var(--shadow-sm)]';
